export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  [key: string]: any; // Allow additional fields
}

export interface AuthResponse<TUser = User> {
  token: string;
  refreshToken: string;
  user: TUser;
  expiresAt: string;
}

export interface PinRequestResponse {
  success: boolean;
  message: string;
  expiresAt?: string;
}

interface MagicLinkAuthConfig {
  apiBaseUrl: string;
}

export class MagicLinkAuthService<TUser extends User = User> {
  private apiBaseUrl: string;

  constructor(config: MagicLinkAuthConfig) {
    this.apiBaseUrl = config.apiBaseUrl;
  }

  /**
   * Request a PIN code to be sent to the user's email
   */
  async requestPin(email: string): Promise<PinRequestResponse> {
    const response = await fetch(`${this.apiBaseUrl}/auth/request-pin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to send PIN' }));
      throw new Error(error.message || 'Failed to send PIN');
    }

    return response.json();
  }

  /**
   * Verify the PIN code and get JWT tokens
   */
  async verifyPin(email: string, pinCode: string): Promise<AuthResponse<TUser>> {
    const response = await fetch(`${this.apiBaseUrl}/auth/verify-pin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, pinCode }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Invalid PIN code' }));
      throw new Error(error.message || 'Invalid PIN code');
    }

    const authResponse: AuthResponse<TUser> = await response.json();
    return authResponse;
  }


  /**
   * Refresh the access token using the refresh token
   * Returns new auth response or null if refresh fails
   */
  async refreshAccessToken(currentRefreshToken: string): Promise<AuthResponse<TUser> | null> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) {
        console.error('[AuthService] Token refresh failed:', response.status);
        return null;
      }

      const authResponse: AuthResponse<TUser> = await response.json();
      return authResponse;
    } catch (error) {
      console.error('[AuthService] Token refresh error:', error);
      return null;
    }
  }


  /**
   * Login with email and password (traditional)
   */
  async login(email: string, password: string): Promise<AuthResponse<TUser>> {
    const response = await fetch(`${this.apiBaseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(error.message || 'Login failed');
    }

    const authResponse: AuthResponse<TUser> = await response.json();
    return authResponse;
  }
}
